import React from 'react';
import { BrowserRouter, Routes, Route, Navigate } from 'react-router-dom';
import { AuthProvider, useAuth } from './context/AuthContext';
import { AppProvider } from './context/AppContext';
import MainLayout from './components/layout/MainLayout';
import LoginPage from './pages/auth/LoginPage';
import RoleDashboard from './pages/dashboard/RoleDashboard';
import StudentList from './pages/students/StudentList';
import NewAdmission from './pages/students/NewAdmission';
import StaffList from './pages/staff/StaffList';
import ClassList from './pages/academic/ClassList';
import SubjectList from './pages/academic/SubjectList';
import Timetable from './pages/academic/Timetable';
import HomeworkList from './pages/academic/HomeworkList';
import HomeworkPage from './pages/homework/HomeworkPage';
import AddHomework from './pages/homework/AddHomework';
import HomeworkReports from './pages/homework/HomeworkReports';
import StudentAttendance from './pages/attendance/StudentAttendance';
import StaffAttendance from './pages/attendance/StaffAttendance';
import AttendanceReports from './pages/attendance/AttendanceReports';
import LessonPlan from './pages/academic/LessonPlan';
import ExamSchedule from './pages/exams/ExamSchedule';
import MarksEntry from './pages/exams/MarksEntry';
import Results from './pages/exams/Results';
import HallTicket from './pages/exams/HallTicket';
import FeeStructure from './pages/fees/FeeStructure';
import FeeCollection from './pages/fees/FeeCollection';
import DueTracking from './pages/fees/DueTracking';
import BookCatalog from './pages/library/BookCatalog';
import IssueReturn from './pages/library/IssueReturn';
import RouteList from './pages/transport/RouteList';
import VehicleList from './pages/transport/VehicleList';
import Announcements from './pages/communication/Announcements';
import Overview from './pages/reports/Overview';
import AdmissionEnquiry from './pages/admission/AdmissionEnquiry';
import AdmissionEnquiryList from './pages/admission/AdmissionEnquiryList';
import UserManagement from './pages/settings/UserManagement';

// Protected layout: checks auth, then renders MainLayout (which has <Outlet />)
function ProtectedLayout() {
  const { currentUser } = useAuth();
  if (!currentUser) return <Navigate to="/login" replace />;
  return <MainLayout />;
}

function AppRoutes() {
  const { currentUser } = useAuth();
  return (
    <Routes>
      <Route
        path="/login"
        element={currentUser ? <Navigate to="/dashboard" replace /> : <LoginPage />}
      />

      {/* All protected pages under MainLayout */}
      <Route element={<ProtectedLayout />}>
        <Route path="/" element={<Navigate to="/dashboard" replace />} />
        <Route path="/dashboard" element={<RoleDashboard />} />

        {/* Admission Enquiries */}
        <Route path="/admission/enquiries" element={<AdmissionEnquiryList />} />
        <Route path="/admission/enquiry" element={<AdmissionEnquiry />} />

        {/* Students */}
        <Route path="/students" element={<StudentList />} />
        <Route path="/students/admission" element={<NewAdmission />} />
        <Route path="/students/allocation" element={<StudentList />} />

        {/* Staff */}
        <Route path="/staff" element={<StaffList />} />
        <Route path="/staff/register" element={<StaffList />} />

        {/* Academic */}
        <Route path="/academic/classes" element={<ClassList />} />
        <Route path="/academic/subjects" element={<SubjectList />} />
        <Route path="/academic/timetable" element={<Timetable />} />
        <Route path="/academic/lesson-plans" element={<LessonPlan />} />
        <Route path="/academic/homework" element={<HomeworkList />} />
        <Route path="/homework" element={<HomeworkPage />} />
        <Route path="/homework/add" element={<AddHomework />} />
        <Route path="/homework/reports" element={<HomeworkReports />} />

        {/* Attendance */}
        <Route path="/attendance/students" element={<StudentAttendance />} />
        <Route path="/attendance/staff" element={<StaffAttendance />} />
        <Route path="/attendance/reports" element={<AttendanceReports />} />

        {/* Exams */}
        <Route path="/exams/schedule" element={<ExamSchedule />} />
        <Route path="/exams/marks" element={<MarksEntry />} />
        <Route path="/exams/results" element={<Results />} />
        <Route path="/exams/hall-ticket" element={<HallTicket />} />

        {/* Fees */}
        <Route path="/fees/structure" element={<FeeStructure />} />
        <Route path="/fees/collection" element={<FeeCollection />} />
        <Route path="/fees/receipts" element={<FeeCollection />} />
        <Route path="/fees/dues" element={<DueTracking />} />

        {/* Communication */}
        <Route path="/communication/announcements" element={<Announcements />} />

        {/* Library */}
        <Route path="/library/catalog" element={<BookCatalog />} />
        <Route path="/library/issue-return" element={<IssueReturn />} />

        {/* Transport */}
        <Route path="/transport/routes" element={<RouteList />} />
        <Route path="/transport/vehicles" element={<VehicleList />} />

        {/* Settings */}
        <Route path="/settings/users" element={<UserManagement />} />

        {/* Reports */}
        <Route path="/reports" element={<Overview />} />
        <Route path="/reports/attendance" element={<AttendanceReports />} />
        <Route path="/reports/financial" element={<Overview />} />

        {/* Catch-all */}
        <Route path="*" element={<Navigate to="/dashboard" replace />} />
      </Route>
    </Routes>
  );
}

function App() {
  return (
    <BrowserRouter>
      <AuthProvider>
        <AppProvider>
          <AppRoutes />
        </AppProvider>
      </AuthProvider>
    </BrowserRouter>
  );
}

export default App;
